import React, { Fragment, useState } from "react";

import ActionButton from "../Buttons/ActionButton";
import DropdownButton from "../Buttons/DropdownButton";
import DropdownItem from "../DropdownItem";
import NotificationsButton from "../NotificationsButton";
import { NavLink } from "react-router-dom";

import { ReactComponent as MenuIcon } from "../../assets/icons/menu.svg";
import { ReactComponent as NotificationsIcon } from "../../assets/icons/notifications.svg";
import { ReactComponent as AccountIcon } from "../../assets/icons/account_circle.svg";
import { ReactComponent as TabletIcon } from "../../assets/icons/tablet.svg";

import Logo from "../../assets/images/boogie_logo.png";
import PowerIoTLogo from "../../assets/images/logo.png";

import MobileMenu from "../MobileMenu";
import { useAuth } from "../../context/AuthContext";
import { useKioskMode } from "../../context/KioskModeContext";
import { useModuleAccess } from "../../hooks/useModuleAccess";
import LocaleSwitcher from "../LocaleSwitcher/LocaleSwitcher";
import TenantSwitcher from "../TenantSwitcher/TenantSwitcher";
import useMe from "../../hooks/useMe";

export default function AppBar() {
  const { logout } = useAuth();
  const { me } = useMe();
  const { enableKioskMode } = useKioskMode();
  const { landingPath } = useModuleAccess();
  const [menu, setMenu] = useState(false);

  const clickHandler = (e: React.MouseEvent) => {
    setMenu(!menu);
    e.preventDefault();
  };

  const handleLogout = logout || (() => localStorage.removeItem("user"));

  // Identity comes from GET /api/admin/me, not the `user` socket event: the
  // socket needed a connection and a getUser round trip, so the bar showed a
  // "User" placeholder until it resolved and forever if it never connected.
  // Render nothing rather than a placeholder while the request is in flight.
  const userName = me?.displayName ?? null;
  const roleName = me?.roleName ?? null;
  // The tenant in view: the impersonated one for an admin who picked a bakery,
  // otherwise their own. actingAccountName is null unless impersonating.
  const tenantName = me?.actingAccountName ?? me?.homeAccountName ?? null;

  return (
    <div className="app-bar">
      <div
        onClick={clickHandler}
        className={`menu-button ${menu ? "open" : ""}`}
      >
        <ActionButton icon={<MenuIcon />} className="app-bar-action" />
      </div>
      {menu && <MobileMenu onClick={clickHandler} />}
      <div className="title">
        <div className="logo">
          <NavLink to={landingPath} className="nav-link">
            <img className="logo-img logo-boogie" src={Logo} alt="Logo" />
            <img className="logo-img logo-poweriot" src={PowerIoTLogo} alt="PowerIoT Logo" />
          </NavLink>
        </div>
      </div>
      <div className="user-actions">
        {/* Always-visible identity: who is signed in, their role, and the
            tenant whose data is on screen. Previously all three were hidden
            behind the account dropdown, and the name read "User". */}
        {userName && (
          <div className="app-bar-identity" title={me?.email ?? undefined}>
            <span className="app-bar-identity-name">{userName}</span>
            {(tenantName || roleName) && (
              <span className="app-bar-identity-meta">
                {[tenantName, roleName].filter(Boolean).join(" · ")}
              </span>
            )}
          </div>
        )}
        <TenantSwitcher />
        <LocaleSwitcher />
        <div onClick={enableKioskMode} title="Enter Kiosk Mode">
          <ActionButton icon={<TabletIcon />} className="app-bar-action" />
        </div>
        <NotificationsButton
          icon={<NotificationsIcon />}
          className="app-bar-action"
        />
        <DropdownButton icon={<AccountIcon />} className="app-bar-action">
          <Fragment>
            <DropdownItem
              icon={<AccountIcon />}
              label={userName ?? me?.email ?? ""}
              to="/profile"
              bottomBorder={true}
            />
            <DropdownItem label="Profile" to="/profile" />
            <DropdownItem
              label="Log out"
              to="/login"
              onClick={handleLogout}
            />
          </Fragment>
        </DropdownButton>
      </div>
    </div>
  );
}
